import React, { useMemo } from 'react'
import {
  useSequencerStore,
  selectCurrentProject,
  SCALE_LABELS,
  midiNoteName,
  type ScaleId,
  type PadMidiRouting,
} from '@/stores/sequencerStore'
import { useUIStore, type PadMode } from '@/stores/uiStore'
import { useWebMidi } from '@/hooks/useWebMidi'

/**
 * Tri-state pad-mode segmented toggle — NORMAL / KEYS / PATTERN.
 *
 *   - NORMAL  → each pad plays its own voice (sequencer default)
 *   - KEYS    → pads become a chromatic / scale keyboard playing the
 *               SOURCE pad's voice (see PR #221 for the source-pad
 *               lock). Keys's existing config panel (scale / root /
 *               channel / output) folds in below when active so users
 *               don't lose any of the previous KeysModeToggle's
 *               affordances.
 *   - PATTERN → SP-404-style pattern select: tap pad N to queue
 *               pattern N for the next downbeat. Cap is 16 per bank.
 *
 * The three modes are mutually exclusive. Flipping to KEYS also
 * turns the project's `keysMode.enabled` on (reusing the source-pad
 * capture we added in the prior PR); flipping away turns it off.
 * Pattern mode has no project-level companion flag today — its
 * behaviour lives entirely in the `SequencerPage.handlePadClick`
 * intercept.
 */
const MODES: ReadonlyArray<{ id: PadMode; label: string }> = [
  { id: 'normal', label: 'Normal' },
  { id: 'keys', label: 'Keys' },
  { id: 'pattern', label: 'Pattern' },
]

export const PadModeToggle: React.FC = () => {
  const project = useSequencerStore(selectCurrentProject)
  const toggleKeysMode = useSequencerStore(s => s.toggleKeysMode)
  const setKeysMode = useSequencerStore(s => s.setKeysMode)
  const padMode = useUIStore(s => s.padMode)
  const setPadMode = useUIStore(s => s.setPadMode)

  const { getSequencerRoutedOutputs } = useWebMidi()
  const routedOutputs = useMemo(
    () => getSequencerRoutedOutputs(),
    [getSequencerRoutedOutputs],
  )

  const cfg = project?.keysMode
  const keysEnabled = cfg?.enabled === true

  const applyMode = (next: PadMode) => {
    if (next === padMode) return
    // Coordinate the project-level `keysMode.enabled` flag with the
    // UI-level padMode so turning Keys on/off from here matches what
    // the fire path expects. Other modes leave keysMode alone when
    // it's already off; they force-disable it when it's on.
    if (next === 'keys' && !keysEnabled) {
      toggleKeysMode()
    } else if (next !== 'keys' && keysEnabled) {
      toggleKeysMode()
    }
    setPadMode(next)
  }

  return (
    <div className="flex items-center gap-2">
      <div
        role="tablist"
        aria-label="Pad mode"
        className="inline-flex items-center gap-0.5 rounded-md border border-brd-subtle bg-surface-sunken p-0.5"
      >
        {MODES.map(({ id, label }) => {
          const active = padMode === id
          return (
            <button
              key={id}
              type="button"
              role="tab"
              aria-selected={active}
              onClick={() => applyMode(id)}
              title={`Pad mode → ${label}`}
              className={`px-2 py-0.5 rounded text-[10px] font-mono uppercase tracking-wider transition-colors ${
                active
                  ? 'bg-accent text-[color:var(--accent-ink)] font-semibold'
                  : 'text-content-secondary hover:text-content-primary'
              }`}
            >
              {label}
            </button>
          )
        })}
      </div>

      {/* Keys sub-panel — same controls the old standalone
          KeysModeToggle exposed. Renders only when Keys is the
          active mode so Normal / Pattern users don't see irrelevant
          config. */}
      {padMode === 'keys' && cfg && (
        <div className="flex items-center gap-1.5 text-[10px] font-mono text-content-secondary bg-surface-sunken border border-brd-subtle rounded-md px-2 py-0.5">
          <select
            value={cfg.scale}
            onChange={e => setKeysMode({ scale: e.target.value as ScaleId })}
            className="bg-transparent focus:outline-none focus:ring-1 focus:ring-[color:var(--accent-halo)] rounded px-0.5"
            aria-label="Scale"
          >
            {(Object.keys(SCALE_LABELS) as ScaleId[]).map(s => (
              <option key={s} value={s}>
                {SCALE_LABELS[s]}
              </option>
            ))}
          </select>
          <span className="text-content-tertiary">·</span>
          <label className="flex items-center gap-1" title="Root note">
            Root
            <input
              type="number"
              min={0}
              max={127}
              value={cfg.rootNote}
              onChange={e =>
                setKeysMode({ rootNote: Number(e.target.value) || 0 })
              }
              className="w-10 bg-surface-raised border border-brd-subtle rounded px-1 text-right text-content-primary focus:outline-none focus:ring-1 focus:ring-[color:var(--accent-halo)]"
            />
            <span className="text-content-tertiary">
              ({midiNoteName(cfg.rootNote)})
            </span>
          </label>
          <span className="text-content-tertiary">·</span>
          <label className="flex items-center gap-1" title="MIDI channel">
            Ch
            <input
              type="number"
              min={1}
              max={16}
              value={cfg.channel}
              onChange={e =>
                setKeysMode({ channel: Number(e.target.value) || 1 })
              }
              className="w-8 bg-surface-raised border border-brd-subtle rounded px-1 text-right text-content-primary focus:outline-none focus:ring-1 focus:ring-[color:var(--accent-halo)]"
            />
          </label>
          <span className="text-content-tertiary">·</span>
          <select
            value={cfg.output ?? '__all__'}
            onChange={e => {
              const v = e.target.value
              if (v === '__all__') setKeysMode({ output: undefined })
              else setKeysMode({ output: v as PadMidiRouting })
            }}
            className="bg-transparent focus:outline-none focus:ring-1 focus:ring-[color:var(--accent-halo)] rounded px-0.5 max-w-[8rem]"
            aria-label="Keys Mode output"
          >
            <option value="__all__">All routed</option>
            <option value="off">Off</option>
            {routedOutputs.map(o => (
              <option key={o.id} value={o.id}>
                {o.available ? o.name : `${o.name} (offline)`}
              </option>
            ))}
          </select>
        </div>
      )}

      {padMode === 'pattern' && (
        <span className="text-[10px] font-mono uppercase tracking-wider text-content-tertiary">
          Tap pad 1-16 → queue pattern
        </span>
      )}
    </div>
  )
}
